import { deleteWorksConcil, useWorksConcils } from '@gozoki/api'
import { includesLowerCase } from '@gozoki/tools'
import { Button, Chip, Paper, TextField, Typography } from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import Page from '../../../components/Page'
import { ConfirmResourceDeletionButton } from '../../../components/communication/ConfirmDeletionButton'
import { useToast } from '../../../components/communication/Toast'
import SimpleDataGrid from '../../../components/containers/SimpleDataGrid'
import PlusIcon from '../../../components/icons/PlusIcon'
import { useRights } from '../../../utils/hooks'
import { getErrorMessageWithSentry } from '../../../utils/sentry'
import { fonts } from '../../../utils/theme/fonts.theme'

export const Route = createFileRoute('/_authenticated/companies/')({
  component: EntreprisesPage,
})

const TAB_WIDTH = 160
const BIGGER_TAB = 220

const worksConcilColDefs: GridColDef[] = [
  {
    field: 'label',
    headerName: 'Label',
    width: TAB_WIDTH,
  },
  {
    field: 'maxMonthAmountPerEmployee',
    headerName: 'Montant max par mois',
    width: BIGGER_TAB,
  },
  { field: 'gozokiPercentage', headerName: 'Pourcentage gozoki', width: BIGGER_TAB },
  {
    field: 'worksConcilPercentage',
    headerName: 'Pourcentage CE',
    width: BIGGER_TAB,
  },
  { field: 'numberOfEmployees', headerName: "Nombre d'employés", width: TAB_WIDTH },
  {
    field: 'numberOfActivesEmployees',
    headerName: 'dont actifs',
    width: TAB_WIDTH,
  },
  {
    field: 'totalUsedAmount',
    headerName: 'Montant total utilisé',
    renderCell: (field) => `${field.value.toLocaleString('fr-FR')} €`,
    width: TAB_WIDTH,
  },
  {
    field: 'isActive',
    headerName: 'Activé',
    renderCell: (field) => (field.value === 'oui' ? <Chip label={field.value} /> : null),
    width: TAB_WIDTH,
  },
]

function EntreprisesPage() {
  const navigate = Route.useNavigate()
  const { data: worksConcils, isLoading: isLoadingWorksConcils, refetch } = useWorksConcils()
  const [Toast, showToast] = useToast()

  const [worksConcilSearch, setWorksConcilSearch] = useState<string>('')
  const [worksConcilsSelection, setWorksConcilsSelection] = useState<number[]>([])

  const isAdmin = useRights(['ADMIN'])

  const concilsWorksRows = useMemo(() => {
    if (worksConcils === undefined) {
      return []
    }
    return worksConcils
      .filter((worksConcil) => includesLowerCase(worksConcil.label, worksConcilSearch))
      .map((worksConcil) => ({
        id: worksConcil.id, // Datagrid requires a unique id field
        label: worksConcil.label,
        maxMonthAmountPerEmployee: worksConcil.maxMonthAmountPerEmployee,
        gozokiPercentage: worksConcil.gozokiPercentage,
        worksConcilPercentage: worksConcil.worksConcilPercentage,
        numberOfEmployees: worksConcil.numberOfEmployees,
        totalUsedAmount: worksConcil.totalUsedAmount,
        numberOfActivesEmployees: worksConcil.numberOfActivesEmployees,
        isActive: worksConcil.isActive ? 'oui' : 'non',
      }))
  }, [worksConcils, worksConcilSearch])

  const { mutate: deleteOne, isPending: isDeleting } = useMutation({
    mutationFn: (id: number) => deleteWorksConcil(id),
    onError: (error) => {
      showToast({
        severity: 'error',
        message: getErrorMessageWithSentry(error),
      })
    },
    onSuccess: () => {
      setWorksConcilsSelection([])
      refetch()
    },
  })

  const handleDeleteWorksConcil = async () => {
    worksConcilsSelection.forEach((id) => {
      deleteOne(id)
    })
  }

  const navigateAndUpdateWorksConcil = () => {
    if (worksConcilsSelection.length === 1) {
      navigate({
        params: { id: worksConcilsSelection[0].toString() },
        to: '/companies/$id/edit',
      })
    } else {
      showToast({
        message: 'Vous devez sélectionner une entreprise',
        severity: 'warning',
      })
    }
  }

  const globalLoading = isLoadingWorksConcils || isDeleting

  return (
    <Page>
      <Toast />
      <div style={{ display: 'flex' }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Partenariats CE
        </Typography>
        <Button
          variant="contained"
          sx={{ marginRight: '16px' }}
          onClick={() => {
            navigate({ to: '/companies/new' })
          }}
          disabled={!isAdmin}
        >
          <PlusIcon />
          Ajouter un partenariat
        </Button>
      </div>
      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', padding: '16px' }}>
          <TextField
            variant="outlined"
            label="Recherche"
            value={worksConcilSearch}
            onChange={(e) => setWorksConcilSearch(e.target.value)}
          />
          <div style={{ flexGrow: 1 }} />

          <ConfirmResourceDeletionButton
            handleResourceDeletion={handleDeleteWorksConcil}
            disabled={worksConcilsSelection.length === 0 || !isAdmin}
          />
          <Button
            variant="contained"
            sx={{ height: '36px' }}
            disabled={globalLoading}
            onClick={navigateAndUpdateWorksConcil}
          >
            {isAdmin ? 'Modifier' : 'Voir'}
          </Button>
        </div>
        <SimpleDataGrid
          columns={worksConcilColDefs}
          rows={concilsWorksRows}
          loading={globalLoading}
          onRowSelectionModelChange={(newSelection) =>
            setWorksConcilsSelection(newSelection as number[])
          }
        />
      </Paper>
    </Page>
  )
}
